import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';

/**
 * Railcar Repair Status Notification — required work-end snapshot form.
 *
 * Opens after the user confirms a Complete transition or after they finish
 * capturing an Issue reason on a line item. Locks immediately on submit
 * and flips the Work Schedule status to COMPLETE / INCOMPLETE server-side.
 *
 * Props:
 *   open, onOpenChange
 *   workScheduleId (required)
 *   outcome         'COMPLETE' | 'INCOMPLETE'
 *   defaultLoadStatus       — pre-fill from SRF (CLEAN / EMPTY / RESIDUE / LOADED / OTHER)
 *   defaultLoadStatusOther  — pre-fill "Other" text from SRF
 *   issueReason (required if outcome === 'INCOMPLETE')
 *   onSubmitted(snapshot)   — called on success so the parent can refresh
 */
const LOAD_STATUS_OPTIONS = [
  { value: 'CLEAN',   label: 'Clean' },
  { value: 'EMPTY',   label: 'Empty' },
  { value: 'RESIDUE', label: 'Residue' },
  { value: 'LOADED',  label: 'Loaded' },
  { value: 'OTHER',   label: 'Other' },
];

const REPAIR_TYPE_OPTIONS = [
  { value: 'PERMANENT',              label: 'Permanent Repair' },
  { value: 'TEMPORARY',              label: 'Temporary Repair' },
  { value: 'PREPPED_FOR_HOME_SHOP',  label: 'Prepped for Home Shop' },
  { value: 'N_A',                    label: 'N/A' },
];

const RailcarRepairStatusNotificationForm = ({
  open,
  onOpenChange,
  workScheduleId,
  outcome,
  defaultLoadStatus = 'CLEAN',
  defaultLoadStatusOther = '',
  issueReason = '',
  onSubmitted,
}) => {
  const { api } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    load_status: defaultLoadStatus,
    load_status_other: defaultLoadStatusOther || '',
    repair_type: '',
    car_spot: '',
    tags_seals_applied: '',
    additional_repair_notes: '',
    site_contact_notified_name: '',
  });

  useEffect(() => {
    if (open) {
      setForm(prev => ({
        ...prev,
        load_status: defaultLoadStatus || 'CLEAN',
        load_status_other: defaultLoadStatusOther || '',
      }));
    }
  }, [open, defaultLoadStatus, defaultLoadStatusOther]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const statusWording = outcome === 'COMPLETE'
    ? 'Status: Complete - Railcar Is Released'
    : 'Status: Incomplete - Hold For Repairs';

  const isValid = () => {
    if (!form.load_status) return false;
    if (form.load_status === 'OTHER' && !form.load_status_other.trim()) return false;
    if (!form.repair_type) return false;
    const req = ['car_spot', 'tags_seals_applied', 'additional_repair_notes',
                 'site_contact_notified_name'];
    return req.every(k => (form[k] || '').trim().length > 0);
  };

  const submit = async () => {
    if (!isValid()) {
      toast.error('All fields are required. Use "N/A" if a value truly does not apply.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        work_schedule_id: workScheduleId,
        outcome,
        issue_reason: outcome === 'INCOMPLETE' ? (issueReason || 'N/A') : null,
        ...form,
      };
      const { data } = await api.post('/railcar-status-notifications', payload);
      toast.success('Railcar Repair Status Notification submitted and locked.');
      onOpenChange(false);
      if (onSubmitted) onSubmitted(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit notification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // No cancel path — the form must be completed once it opens.
        if (!next && !submitting) {
          toast.warning('This form must be completed before continuing.');
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="railcar-status-notification-form"
      >
        <DialogHeader>
          <DialogTitle>Railcar Repair Status Notification</DialogTitle>
          <DialogDescription className="text-[#323033]">
            <span className="font-semibold" data-testid="rrsn-status-wording">
              {statusWording}
            </span>
          </DialogDescription>
        </DialogHeader>

        {outcome === 'INCOMPLETE' && issueReason && (
          <div className="rounded-md border border-[#FDE68A] bg-[#FEF3C7] p-3 text-sm text-[#92400E] flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Captured Issue Reason</div>
              <div>{issueReason}</div>
            </div>
          </div>
        )}

        <div className="space-y-4 pt-2">
          {/* 1. Load Status */}
          <div>
            <Label>Load Status *</Label>
            <RadioGroup
              value={form.load_status}
              onValueChange={(v) => set('load_status', v)}
              className="flex flex-wrap gap-4 pt-1"
            >
              {LOAD_STATUS_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={value}
                    id={`rrsn-load-${value}`}
                    data-testid={`rrsn-load-${value.toLowerCase()}`}
                  />
                  <Label htmlFor={`rrsn-load-${value}`} className="cursor-pointer">{label}</Label>
                </div>
              ))}
            </RadioGroup>
            {form.load_status === 'OTHER' && (
              <div className="mt-2">
                <Label htmlFor="rrsn-load-other">Specify Load Status *</Label>
                <Input
                  id="rrsn-load-other"
                  value={form.load_status_other}
                  maxLength={100}
                  onChange={(e) => set('load_status_other', e.target.value)}
                  data-testid="rrsn-load-other-input"
                />
              </div>
            )}
          </div>

          {/* 2. Car Spot */}
          <div>
            <Label htmlFor="rrsn-car-spot">Car Spot *</Label>
            <Input
              id="rrsn-car-spot"
              maxLength={25}
              value={form.car_spot}
              onChange={(e) => set('car_spot', e.target.value)}
              placeholder="e.g. Track 3, Spot 12"
              data-testid="rrsn-car-spot"
            />
          </div>

          {/* 3. Tags/Seals Applied */}
          <div>
            <Label htmlFor="rrsn-tags-seals">Tags/Seals Applied *</Label>
            <Input
              id="rrsn-tags-seals"
              maxLength={150}
              value={form.tags_seals_applied}
              onChange={(e) => set('tags_seals_applied', e.target.value)}
              placeholder='Enter details, or "N/A"'
              data-testid="rrsn-tags-seals"
            />
          </div>

          {/* 4. Additional Repair Notes */}
          <div>
            <Label htmlFor="rrsn-notes">Additional Repair Notes *</Label>
            <Textarea
              id="rrsn-notes"
              maxLength={300}
              value={form.additional_repair_notes}
              onChange={(e) => set('additional_repair_notes', e.target.value)}
              rows={3}
              placeholder='Enter notes, or "N/A"'
              data-testid="rrsn-notes"
            />
            <div className="text-xs text-[#5A5A5A] text-right mt-1">
              {form.additional_repair_notes.length}/300
            </div>
          </div>

          {/* 5. Repair Type (new — approver-invisible, tech-owned) */}
          <div>
            <Label>Repair Type *</Label>
            <Select
              value={form.repair_type}
              onValueChange={(v) => set('repair_type', v)}
            >
              <SelectTrigger data-testid="rrsn-repair-type">
                <SelectValue placeholder="Select repair type" />
              </SelectTrigger>
              <SelectContent>
                {REPAIR_TYPE_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value} data-testid={`rrsn-repair-type-${value.toLowerCase()}`}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 6. Site Contact Notified Name (last) */}
          <div>
            <Label htmlFor="rrsn-site-contact">Site Contact Notified Name *</Label>
            <Input
              id="rrsn-site-contact"
              maxLength={50}
              value={form.site_contact_notified_name}
              onChange={(e) => set('site_contact_notified_name', e.target.value)}
              placeholder='Full name, or "N/A"'
              data-testid="rrsn-site-contact"
            />
          </div>

          <div
            className="rounded-md border-l-4 border-[#D97706] bg-[#FEF3C7] p-3 flex items-start gap-3"
            data-testid="rrsn-lock-notice"
          >
            <AlertCircle className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-[#92400E] uppercase tracking-wide">
                Locked After Submission
              </div>
              <div className="text-sm text-[#92400E] mt-0.5">
                Once submitted, this work order event record is permanently locked and cannot be changed.
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={submit}
            disabled={submitting || !isValid()}
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            data-testid="rrsn-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Submit Status Notification
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RailcarRepairStatusNotificationForm;
